/**
 * Module Launcher
 * Home screen showing 4 module cards
 * Each card has: title, description, key badges (counts)
 * Clicking a card enters that module
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Home, Building2, GitBranch, Wrench, PoundSterling,
  ChevronRight, LogOut, LayoutDashboard, Lock
} from 'lucide-react';
import { API } from '@/App';
import { 
  MODULES, 
  MODULE_INFO, 
  ACCOUNTS_ALLOWED_ROLES,
  canAccessModule,
  isModuleEnabled
} from '@/config/modules';

// Icon mapping
const ICONS = {
  Home,
  Building2,
  GitBranch,
  Wrench,
  PoundSterling,
  LayoutDashboard,
};

// Color schemes for modules
const MODULE_COLORS = {
  emerald: {
    bg: 'bg-emerald-500/10 hover:bg-emerald-500/20',
    border: 'border-emerald-500/30 hover:border-emerald-500/50',
    icon: 'text-emerald-400',
    badge: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  },
  blue: {
    bg: 'bg-blue-500/10 hover:bg-blue-500/20',
    border: 'border-blue-500/30 hover:border-blue-500/50',
    icon: 'text-blue-400',
    badge: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  },
  violet: {
    bg: 'bg-violet-500/10 hover:bg-violet-500/20',
    border: 'border-violet-500/30 hover:border-violet-500/50',
    icon: 'text-violet-400',
    badge: 'bg-violet-500/20 text-violet-300 border-violet-500/30',
  },
  amber: {
    bg: 'bg-amber-500/10 hover:bg-amber-500/20',
    border: 'border-amber-500/30 hover:border-amber-500/50',
    icon: 'text-amber-400',
    badge: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  },
  slate: {
    bg: 'bg-slate-500/10 hover:bg-slate-500/20',
    border: 'border-slate-500/30 hover:border-slate-500/50',
    icon: 'text-slate-400',
    badge: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
  },
};

export default function ModuleLauncher() {
  const navigate = useNavigate();
  const [userName, setUserName] = useState('');
  const [user, setUser] = useState(null);
  const [moduleCounts, setModuleCounts] = useState({});
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      setUser(userData);
      setUserName(userData.name || userData.email?.split('@')[0] || 'User');
    }
    fetchModuleCounts();
  }, []);
  
  const fetchModuleCounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/module-counts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModuleCounts(response.data);
    } catch (error) {
      console.error('Error fetching module counts:', error);
      // Use fallback counts
      setModuleCounts({
        sales: { active_listings: 0, pending_offers: 0, new_applicants: 0 },
        progression: { active_cases: 0, pending_milestones: 0, awaiting_exchange: 0 },
        lettings: { managed_properties: 0, tenancies: 0, compliance_due: 0 },
        maintenance: { open_jobs: 0, pending_quotes: 0, unpaid_invoices: 0 },
        accounts: { pending_statements: 0, unpaid_invoices: 0 },
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Main 4 modules (not accounts)
  const mainModules = [
    MODULES.SALES,
    MODULES.PROGRESSION,
    MODULES.LETTINGS,
    MODULES.MAINTENANCE,
  ];
  
  // Check accounts access
  const canAccessAccounts = user && canAccessModule(MODULES.ACCOUNTS, user);
  
  const handleModuleClick = (moduleId) => {
    // Don't navigate if module is disabled for this user
    if (!isModuleEnabled(moduleId, user)) {
      return;
    }
    const moduleInfo = MODULE_INFO[moduleId];
    if (moduleInfo) {
      navigate(moduleInfo.defaultPath);
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('goodacres_last_module');
    navigate('/login');
  };
  
  const getBadgeLabel = (key) => {
    const labels = {
      active_listings: 'Active',
      pending_offers: 'Offers',
      new_applicants: 'Applicants',
      active_cases: 'Cases',
      pending_milestones: 'Pending',
      awaiting_exchange: 'Exchange',
      managed_properties: 'Properties',
      tenancies: 'Tenancies',
      compliance_due: 'Compliance',
      open_jobs: 'Jobs',
      pending_quotes: 'Quotes',
      unpaid_invoices: 'Invoices',
      pending_statements: 'Statements',
    };
    return labels[key] || key;
  };
  
  const renderModuleCard = (moduleId) => {
    const info = MODULE_INFO[moduleId];
    const colors = MODULE_COLORS[info.color];
    const Icon = ICONS[info.icon] || LayoutDashboard;
    const counts = moduleCounts[moduleId] || {};
    const isEnabled = isModuleEnabled(moduleId, user);
    const isDisabledForUser = !isEnabled;
    
    // Hide completely disabled modules from non-admin users
    if (isDisabledForUser) {
      return (
        <Card
          key={moduleId}
          className={`
            relative transition-all duration-300 border-2
            bg-slate-800/30 border-slate-700/30
            p-6 rounded-xl cursor-not-allowed opacity-60
          `}
          data-testid={`module-card-${moduleId}-disabled`}
        >
          {/* Icon */}
          <div className="w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center mb-4 text-slate-500">
            <Icon className="w-6 h-6" />
          </div>
          
          {/* Title & Description */}
          <h3 className="text-xl font-semibold text-slate-400 mb-1">{info.title}</h3>
          <p className="text-sm text-slate-600 mb-4">{info.description}</p>
          
          {/* Coming Soon Badge */}
          <Badge variant="outline" className="text-xs bg-slate-700/50 text-slate-400 border-slate-600">
            <Lock className="w-3 h-3 mr-1" />
            Coming Soon
          </Badge>
        </Card>
      );
    }
    
    return (
      <Card
        key={moduleId}
        onClick={() => handleModuleClick(moduleId)}
        className={`
          relative cursor-pointer transition-all duration-300 border-2
          ${colors.bg} ${colors.border}
          p-6 rounded-xl group
        `}
        data-testid={`module-card-${moduleId}`}
      >
        {/* Icon */}
        <div className={`w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center mb-4 ${colors.icon}`}>
          <Icon className="w-6 h-6" />
        </div>
        
        {/* Title & Description */}
        <h3 className="text-xl font-semibold text-white mb-1">{info.title}</h3>
        <p className="text-sm text-white/50 mb-4">{info.description}</p>
        
        {/* Badges */}
        <div className="flex flex-wrap gap-2">
          {info.badgeKeys.map(key => {
            const count = counts[key];
            if (count === undefined) return null;
            return (
              <Badge 
                key={key}
                variant="outline"
                className={`text-xs ${colors.badge}`}
              >
                {count} {getBadgeLabel(key)}
              </Badge>
            );
          })}
        </div>
        
        {/* Arrow indicator */}
        <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
          <ChevronRight className="w-5 h-5 text-white/50" />
        </div>
      </Card>
    );
  };
  
  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-black to-slate-900" />
      
      {/* Content */}
      <div className="relative z-10 min-h-screen">
        {/* Header */}
        <header className="flex items-center justify-between px-8 py-6">
          <div className="flex items-center gap-4">
            <img 
              src="/goodacres-nav-logo.svg" 
              alt="Goodacres" 
              className="h-8"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-white/50">
              {userName}
            </span>
            <button
              onClick={handleLogout}
              className="text-white/30 hover:text-white/60 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </header>
        
        {/* Main content */}
        <main className="px-8 py-12">
          <div className="max-w-6xl mx-auto">
            {/* Welcome */}
            <div className="mb-12">
              <p className="text-sm text-white/40 uppercase tracking-widest mb-2">Welcome back</p>
              <h1 className="text-4xl font-light text-white">{userName}</h1>
            </div>
            
            {/* Module grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {mainModules.map(moduleId => renderModuleCard(moduleId))}
            </div>
            
            {/* Accounts card (if accessible) */}
            {canAccessAccounts && (
              <div className="mt-6">
                <p className="text-xs text-white/30 uppercase tracking-widest mb-3">Restricted Access</p>
                <div className="max-w-md">
                  {renderModuleCard(MODULES.ACCOUNTS)}
                </div>
              </div>
            )}
            
            {/* Quick links */}
            <div className="mt-12 pt-8 border-t border-white/10">
              <div className="flex items-center gap-6">
                <button
                  onClick={() => navigate('/my-dashboard')}
                  className="text-sm text-white/40 hover:text-white/70 transition-colors"
                >
                  My Dashboard
                </button>
                <button
                  onClick={() => navigate('/contacts')}
                  className="text-sm text-white/40 hover:text-white/70 transition-colors"
                >
                  Contacts
                </button>
                <button
                  onClick={() => navigate('/diary')}
                  className="text-sm text-white/40 hover:text-white/70 transition-colors"
                >
                  Diary
                </button>
                {user && ['admin', 'director'].includes(user.role) && (
                  <button
                    onClick={() => navigate('/staff')}
                    className="text-sm text-white/40 hover:text-white/70 transition-colors"
                  >
                    Staff
                  </button>
                )}
              </div>
            </div>
          </div>
        </main>
        
        {/* Footer logo */}
        <div className="absolute bottom-8 right-8">
          <img 
            src="/goodacres-full-logo.jpg" 
            alt="Goodacres" 
            className="w-48 opacity-30"
          />
        </div>
      </div>
    </div>
  );
}
